/**
 * Registry of live onSnapshot unsubscribe functions.
 *
 * Why: signing out while listeners are still attached makes the backend
 * re-evaluate every live listen without auth — each one logs a server-side
 * permission-denied (the "5 unexplained prod errors" burst in the 2026-07
 * audit). handleLogout calls detachAllSnapshots() BEFORE signOut so no
 * listener ever outlives its token.
 *
 * Usage in a component:
 *   useEffect(() => registerSnapshot(onSnapshot(q, onNext, onError)), []);
 * The returned cleanup both deregisters and unsubscribes; Firestore
 * unsubscribe functions are safe to call more than once.
 */
const active = new Set();

export function registerSnapshot(unsubscribe) {
  active.add(unsubscribe);
  return () => {
    active.delete(unsubscribe);
    unsubscribe();
  };
}

export function detachAllSnapshots() {
  const pending = Array.from(active);
  active.clear();
  pending.forEach((unsubscribe) => {
    try {
      unsubscribe();
    } catch (err) {
      console.error('Failed to detach a Firestore listener:', err);
    }
  });
}

/** Test hook: how many listeners are currently registered? */
export function activeSnapshotCount() {
  return active.size;
}
